import React, { useState, useEffect } from "react";
import { Ledger, Voucher, GROUP_NATURE } from "@/lib/accounting/types";
import { useAccounting } from "@/contexts/AccountingContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Pencil,
  BookOpen,
  Building2,
  Phone,
  Mail,
  MapPin,
  Calendar,
  ArrowUpRight,
  ArrowDownLeft,
  ShieldCheck,
  FileText,
  RotateCw,
  ExternalLink
} from "lucide-react";
import { useNavigate } from "react-router-dom";

export interface LedgerDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ledger: Ledger | null;
  onEditClick: (ledger: Ledger) => void;
  canEdit?: boolean;
}

export const LedgerDetailDialog: React.FC<LedgerDetailDialogProps> = ({
  open,
  onOpenChange,
  ledger,
  onEditClick,
  canEdit = true,
}) => {
  const { vouchers } = useAccounting();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");

  if (!ledger) return null;

  // Filter vouchers that post to this ledger
  const relatedVouchers = vouchers.filter((v) =>
    v.lines.some((l) => l.ledgerId === ledger.id)
  );

  // Calculate transaction statistics
  let totalDebitPosted = 0;
  let totalCreditPosted = 0;

  // Flatten transaction entries with running balance
  const isNormalDr = GROUP_NATURE[ledger.group] === "Asset" || GROUP_NATURE[ledger.group] === "Expense";
  let runningSigned = (ledger.openingBalanceType === "Cr" ? -1 : 1) * Number(ledger.openingBalance || 0);

  const entries: Array<{
    voucherId: string;
    voucherNumber: string;
    date: string;
    type: string;
    narration: string;
    debit: number;
    credit: number;
    runningBalance: number;
    runningType: "Dr" | "Cr";
  }> = [];

  // Sort vouchers chronologically
  const sorted = [...relatedVouchers].sort((a, b) =>
    (a.date || "").localeCompare(b.date || "")
  );

  for (const v of sorted) {
    const linesForLedger = v.lines.filter((l) => l.ledgerId === ledger.id);
    for (const l of linesForLedger) {
      const d = Number(l.debit || 0);
      const c = Number(l.credit || 0);
      totalDebitPosted += d;
      totalCreditPosted += c;

      runningSigned += (d - c);
      const balVal = Math.abs(runningSigned);
      const balType: "Dr" | "Cr" = isNormalDr
        ? runningSigned >= 0 ? "Dr" : "Cr"
        : runningSigned <= 0 ? "Cr" : "Dr";

      entries.push({
        voucherId: v.id,
        voucherNumber: v.voucherNumber,
        date: v.date,
        type: v.type,
        narration: l.narration || v.narration || "",
        debit: d,
        credit: c,
        runningBalance: Math.round(balVal * 100) / 100,
        runningType: balType,
      });
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto p-0 gap-0 border-slate-200">
        {/* Header */}
        <DialogHeader className="p-5 pb-4 border-b border-slate-100 bg-slate-50/70">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-50 border border-blue-200 text-blue-700 flex items-center justify-center shrink-0 shadow-2xs">
                <BookOpen className="w-5 h-5" />
              </div>
              <div>
                <DialogTitle className="text-lg font-bold text-slate-900 flex items-center gap-2 flex-wrap">
                  <span>{ledger.name}</span>
                  {ledger.isSystem && (
                    <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700 border-blue-200 gap-1 font-semibold">
                      <ShieldCheck className="w-3 h-3" /> System Control Account
                    </Badge>
                  )}
                </DialogTitle>
                <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5 flex-wrap">
                  <Badge variant="outline" className="text-[11px] bg-white border-slate-200 text-slate-700 font-medium">
                    {ledger.group}
                  </Badge>
                  <span className="text-slate-300">•</span>
                  <span className="font-semibold text-slate-600">{GROUP_NATURE[ledger.group]}</span>
                  <span className="text-slate-300">•</span>
                  <span className="font-mono text-[11px] text-slate-400">ID: {ledger.id}</span>
                </div>
              </div>
            </div>

            {/* Prominent Edit Ledger Action Button */}
            {canEdit && (
              <Button
                size="sm"
                onClick={() => {
                  onOpenChange(false);
                  onEditClick(ledger);
                }}
                className="h-8 text-xs gap-1.5 bg-blue-600 text-white hover:bg-blue-700 font-semibold shadow-2xs shrink-0 self-start sm:self-auto px-3.5"
                title="Edit this ledger account details"
              >
                <Pencil className="w-3.5 h-3.5" />
                <span>Edit Ledger</span>
              </Button>
            )}
          </div>
        </DialogHeader>

        {/* Content Tabs */}
        <div className="p-5 sm:p-6 space-y-5">
          {/* Quick Metrics Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {/* Current Balance */}
            <div className="p-3.5 rounded-xl bg-white border border-slate-200 shadow-2xs">
              <span className="text-[11px] font-medium text-slate-400 block mb-1">Current Balance</span>
              <div className="flex items-baseline gap-1">
                <span className="text-base sm:text-lg font-black text-slate-900 tracking-tight">
                  ₹{Number(ledger.currentBalance || 0).toLocaleString("en-IN")}
                </span>
                <span className="text-xs font-bold text-blue-600">{ledger.currentBalanceType}</span>
              </div>
            </div>

            {/* Opening Balance */}
            <div className="p-3.5 rounded-xl bg-white border border-slate-200 shadow-2xs">
              <span className="text-[11px] font-medium text-slate-400 block mb-1">Opening Balance</span>
              <div className="flex items-baseline gap-1">
                <span className="text-base sm:text-lg font-bold text-slate-700 tracking-tight">
                  ₹{Number(ledger.openingBalance || 0).toLocaleString("en-IN")}
                </span>
                <span className="text-xs font-medium text-slate-500">{ledger.openingBalanceType}</span>
              </div>
            </div>

            {/* Total Debit Posted */}
            <div className="p-3.5 rounded-xl bg-emerald-50/50 border border-emerald-100 shadow-2xs">
              <span className="text-[11px] font-medium text-emerald-700 flex items-center gap-1 mb-1">
                <ArrowDownLeft className="w-3 h-3 text-emerald-600" /> Total Debits
              </span>
              <span className="text-base sm:text-lg font-bold text-emerald-900 tracking-tight">
                ₹{totalDebitPosted.toLocaleString("en-IN")}
              </span>
            </div>

            {/* Total Credit Posted */}
            <div className="p-3.5 rounded-xl bg-purple-50/50 border border-purple-100 shadow-2xs">
              <span className="text-[11px] font-medium text-purple-700 flex items-center gap-1 mb-1">
                <ArrowUpRight className="w-3 h-3 text-purple-600" /> Total Credits
              </span>
              <span className="text-base sm:text-lg font-bold text-purple-900 tracking-tight">
                ₹{totalCreditPosted.toLocaleString("en-IN")}
              </span>
            </div>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="bg-slate-100/90 p-1 border border-slate-200/80">
              <TabsTrigger value="overview" className="text-xs">
                Account Information
              </TabsTrigger>
              <TabsTrigger value="transactions" className="text-xs">
                Transaction Statement ({entries.length})
              </TabsTrigger>
            </TabsList>

            {/* Tab 1: Account Information */}
            <TabsContent value="overview" className="space-y-4 pt-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* GST & Statutory Card */}
                <div className="p-4 rounded-xl border border-slate-200 bg-white space-y-3 shadow-2xs">
                  <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1.5 pb-2 border-b border-slate-100">
                    <Building2 className="w-3.5 h-3.5 text-blue-600" />
                    GST & Statutory Details
                  </h4>
                  <div className="space-y-2 text-xs">
                    <div className="flex justify-between py-1 border-b border-slate-50">
                      <span className="text-slate-500">GST Registration:</span>
                      <span className="font-semibold text-slate-800">
                        {ledger.gstApplicable || ledger.gstin ? (
                          <span className="text-emerald-600">Registered</span>
                        ) : (
                          <span className="text-slate-400">Not Applicable</span>
                        )}
                      </span>
                    </div>
                    {ledger.gstin && (
                      <div className="flex justify-between py-1 border-b border-slate-50">
                        <span className="text-slate-500">GSTIN:</span>
                        <span className="font-mono font-bold text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded text-[11px]">
                          {ledger.gstin}
                        </span>
                      </div>
                    )}
                    {ledger.gstRate !== undefined && ledger.gstRate !== null && (
                      <div className="flex justify-between py-1 border-b border-slate-50">
                        <span className="text-slate-500">Default GST Rate:</span>
                        <span className="font-semibold text-slate-800">{ledger.gstRate}%</span>
                      </div>
                    )}
                    {ledger.hsnCode && (
                      <div className="flex justify-between py-1">
                        <span className="text-slate-500">HSN / SAC Code:</span>
                        <span className="font-mono text-slate-700">{ledger.hsnCode}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Contact & Address Card */}
                <div className="p-4 rounded-xl border border-slate-200 bg-white space-y-3 shadow-2xs">
                  <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1.5 pb-2 border-b border-slate-100">
                    <Phone className="w-3.5 h-3.5 text-blue-600" />
                    Contact & Address
                  </h4>
                  <div className="space-y-2 text-xs">
                    <div className="flex justify-between py-1 border-b border-slate-50">
                      <span className="text-slate-500">Contact Person:</span>
                      <span className="font-medium text-slate-800">{ledger.contactPerson || "—"}</span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-slate-50">
                      <span className="text-slate-500">Phone:</span>
                      <span className="font-medium text-slate-800">{ledger.phone || "—"}</span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-slate-50">
                      <span className="text-slate-500">Email:</span>
                      <span className="font-medium text-slate-800">{ledger.email || "—"}</span>
                    </div>
                    <div className="py-1">
                      <span className="text-slate-500 block mb-0.5">Address:</span>
                      <span className="text-slate-700 leading-relaxed">
                        {[ledger.address, ledger.state, ledger.pincode].filter(Boolean).join(", ") || "—"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Tab 2: Transaction Statement */}
            <TabsContent value="transactions" className="space-y-3 pt-3">
              {entries.length === 0 ? (
                <div className="p-8 text-center rounded-xl bg-slate-50 border border-dashed border-slate-200 space-y-2">
                  <FileText className="w-8 h-8 text-slate-300 mx-auto" />
                  <p className="text-xs font-semibold text-slate-700">No transactions recorded yet</p>
                  <p className="text-[11px] text-slate-400 max-w-sm mx-auto">
                    When invoices, receipts, payments, or journal vouchers post to this account, they will automatically appear here with live running balances.
                  </p>
                </div>
              ) : (
                <div className="rounded-xl border border-slate-200 overflow-hidden shadow-2xs">
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs text-left">
                      <thead className="bg-slate-50 text-slate-600 border-b border-slate-200">
                        <tr>
                          <th className="py-2.5 px-3 font-semibold">Date</th>
                          <th className="py-2.5 px-3 font-semibold">Voucher No</th>
                          <th className="py-2.5 px-3 font-semibold">Type</th>
                          <th className="py-2.5 px-3 font-semibold">Narration</th>
                          <th className="py-2.5 px-3 font-semibold text-right">Debit (₹)</th>
                          <th className="py-2.5 px-3 font-semibold text-right">Credit (₹)</th>
                          <th className="py-2.5 px-3 font-semibold text-right">Balance (₹)</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {/* Opening Balance Row */}
                        <tr className="bg-slate-50/50 font-medium">
                          <td className="py-2 px-3 text-slate-400">—</td>
                          <td className="py-2 px-3 text-slate-500">OPENING</td>
                          <td className="py-2 px-3 text-slate-500">Opening Balance</td>
                          <td className="py-2 px-3 text-slate-400 italic">Initial Balance</td>
                          <td className="py-2 px-3 text-right">
                            {ledger.openingBalanceType === "Dr" ? `₹${Number(ledger.openingBalance || 0).toLocaleString("en-IN")}` : "—"}
                          </td>
                          <td className="py-2 px-3 text-right">
                            {ledger.openingBalanceType === "Cr" ? `₹${Number(ledger.openingBalance || 0).toLocaleString("en-IN")}` : "—"}
                          </td>
                          <td className="py-2 px-3 text-right font-bold text-slate-800">
                            ₹{Number(ledger.openingBalance || 0).toLocaleString("en-IN")} {ledger.openingBalanceType}
                          </td>
                        </tr>

                        {/* Transaction Rows */}
                        {entries.map((e, idx) => (
                          <tr key={`${e.voucherId}-${idx}`} className="hover:bg-slate-50/60 transition-colors">
                            <td className="py-2 px-3 font-mono text-slate-600 whitespace-nowrap">{e.date}</td>
                            <td className="py-2 px-3 font-mono font-semibold text-blue-600 whitespace-nowrap">
                              {e.voucherNumber}
                            </td>
                            <td className="py-2 px-3 whitespace-nowrap">
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0 font-medium bg-white">
                                {e.type}
                              </Badge>
                            </td>
                            <td className="py-2 px-3 text-slate-600 max-w-[200px] truncate" title={e.narration}>
                              {e.narration || "—"}
                            </td>
                            <td className="py-2 px-3 text-right font-mono text-emerald-700 whitespace-nowrap">
                              {e.debit > 0 ? `₹${e.debit.toLocaleString("en-IN")}` : "—"}
                            </td>
                            <td className="py-2 px-3 text-right font-mono text-purple-700 whitespace-nowrap">
                              {e.credit > 0 ? `₹${e.credit.toLocaleString("en-IN")}` : "—"}
                            </td>
                            <td className="py-2 px-3 text-right font-mono font-bold text-slate-900 whitespace-nowrap">
                              ₹{e.runningBalance.toLocaleString("en-IN")} <span className="text-[10px] text-slate-500 font-sans">{e.runningType}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  );
};
